import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import { GUIDES, PARTS, guideBySlug } from './guides';
import { GUIDE_SEO, INDEX_SEO } from './guideSeo';
import { hideSiteFaq } from '../utils/siteFaq';
import './GuidePage.css';

// ownezzy.com/guide (every guide) and /guide/<slug> (one guide, step by step). Each step has its own link
// (/guide/<slug>#<step id>) with a "Copy link" button, so support can send the exact step.

const SITE = 'https://ownezzy.com';

// **bold** and [label](url) inside a point. A link to a guide that is not written yet shows as plain bold text.
function Rich({ text }) {
  const parts = [];
  const re = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1]) parts.push(<b key={m.index}>{m[1]}</b>);
    else {
      const [, , label, url] = m;
      const guideSlug = url.startsWith('/guide/') ? url.slice(7).split('#')[0] : '';
      if (guideSlug && !guideBySlug(guideSlug)) parts.push(<b key={m.index}>{label}</b>);
      else parts.push(<a key={m.index} href={url} {...(url.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{label}</a>);
    }
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// The same text without **bold** and link marks, for search engines
const plain = (text) => text.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
const pointText = (p) => plain(typeof p === 'string' ? p : p.text);

function Points({ points, level = 0 }) {
  return (
    <ul className={`gd-points gd-level-${level}`}>
      {points.map((p, i) => (
        <li key={i}>
          <Rich text={typeof p === 'string' ? p : p.text} />
          {/* Only objects have sub-points: every string has a built-in .sub() method */}
          {typeof p !== 'string' && Array.isArray(p.sub) && <Points points={p.sub} level={level + 1} />}
        </li>
      ))}
    </ul>
  );
}

function CopyLink({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1800); };
    if (navigator.clipboard) navigator.clipboard.writeText(url).then(done).catch(() => window.prompt('Copy this link', url));
    else window.prompt('Copy this link', url);
  };
  return <button type="button" className="gd-copy" onClick={copy} title={url}>{copied ? '✓ Copied' : '🔗 Copy link'}</button>;
}

// The step's screenshots: the computer view and the phone view side by side (one under the other on a phone)
function Shots({ guide, step }) {
  const base = `/guide-img/${guide.slug}/${step.id}`; // not under /guide: those addresses are the guide pages
  return (
    <div className={`gd-shots ${step.shots === 'desktop' ? 'gd-shots-one' : ''}`}>
      <figure className="gd-shot gd-desktop">
        <img src={`${base}-desktop.webp`} alt={`${step.title}: ${guide.title}, Ownezzy on a computer`} loading="lazy" />
        <figcaption>💻 Computer</figcaption>
      </figure>
      {step.shots !== 'desktop' && (
        <figure className="gd-shot gd-mobile">
          <img src={`${base}-mobile.webp`} alt={`${step.title}: ${guide.title}, Ownezzy on a phone`} loading="lazy" />
          <figcaption>📱 Phone</figcaption>
        </figure>
      )}
    </div>
  );
}

function setMeta(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}
function setPageMeta(title, description, url) {
  document.title = title;
  setMeta('meta[name="description"]', 'content', description);
  setMeta('link[rel="canonical"]', 'href', url);
  setMeta('meta[property="og:url"]', 'content', url);
  setMeta('meta[property="og:title"]', 'content', title);
  setMeta('meta[property="og:description"]', 'content', description);
  setMeta('meta[name="twitter:title"]', 'content', title);
  setMeta('meta[name="twitter:description"]', 'content', description);
}

// Structured data for search engines: the breadcrumb trail, the guide's steps (HowTo) and its questions (FAQPage)
function structuredData(guide, seo, url) {
  const crumbs = [{ name: 'Home', item: `${SITE}/` }, { name: 'User Guide', item: `${SITE}/guide` }];
  if (guide) crumbs.push({ name: guide.title, item: url });
  const graph = [{ '@type': 'BreadcrumbList', itemListElement: crumbs.map((c, i) => ({ '@type': 'ListItem', position: i + 1, ...c })) }];
  if (guide) {
    graph.push({
      '@type': 'HowTo',
      name: guide.title,
      description: seo.description,
      url,
      inLanguage: 'en-IN',
      step: guide.steps.map((step, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: step.title,
        url: `${url}#${step.id}`,
        text: step.points.map(pointText).join(' '),
        ...(step.shots ? { image: `${SITE}/guide-img/${guide.slug}/${step.id}-desktop.webp` } : {}),
      })),
    });
  }
  if (seo.faqs?.length) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: seo.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: plain(f.a) } })),
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

function Faqs({ faqs }) {
  if (!faqs?.length) return null;
  return (
    <section className="gd-faqs">
      <h2>Common questions</h2>
      {faqs.map((f) => (
        <div key={f.q} className="gd-faq">
          <h3>{f.q}</h3>
          <p><Rich text={f.a} /></p>
        </div>
      ))}
    </section>
  );
}

function Sidebar({ current }) {
  return (
    <nav className="gd-side" aria-label="User Guide">
      <a className="gd-side-home" href="/guide">📘 User Guide</a>
      {PARTS.map((part) => (
        <div key={part.id} className="gd-side-part">
          <b>{part.icon} {part.title}</b>
          {GUIDES.filter((g) => g.part === part.id).map((g) => (
            <div key={g.slug}>
              <a className={g.slug === current?.slug ? 'is-current' : ''} href={`/guide/${g.slug}`}>{g.title}</a>
              {g.slug === current?.slug && (
                <div className="gd-side-steps">
                  {g.steps.map((s, i) => <a key={s.id} href={`#${s.id}`}>{i + 1}. {s.title}</a>)}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </nav>
  );
}

function GuideIndex() {
  return (
    <div className="gd-index">
      <span className="gd-kicker">USER GUIDE</span>
      <h1>How to create a website for your shop, step by step</h1>
      <p className="gd-intro">{INDEX_SEO.intro}</p>
      <p>From finding a website to running your store every day. Every guide and every step has its own link you can share.</p>
      {PARTS.map((part) => (
        <section key={part.id} className="gd-part">
          <h2>{part.icon} {part.title}</h2>
          <p>{part.text}</p>
          <div className="gd-cards">
            {GUIDES.filter((g) => g.part === part.id).map((g) => (
              <a key={g.slug} className="gd-card" href={`/guide/${g.slug}`}>
                <b>{g.title}</b>
                <span>{g.summary}</span>
                <ol>{g.steps.map((s) => <li key={s.id}>{s.title}</li>)}</ol>
                <em>Open guide →</em>
              </a>
            ))}
          </div>
        </section>
      ))}
      <Faqs faqs={INDEX_SEO.faqs} />
    </div>
  );
}

function GuideView({ guide }) {
  const index = GUIDES.indexOf(guide);
  const prev = GUIDES[index - 1];
  const next = GUIDES[index + 1];
  const part = PARTS.find((p) => p.id === guide.part);
  const seo = GUIDE_SEO[guide.slug] || {};
  const related = GUIDES.filter((g) => g.part === guide.part && g !== guide);
  return (
    <article className="gd-article">
      <nav className="gd-crumbs" aria-label="Breadcrumb"><a href="/">Home</a> / <a href="/guide">User Guide</a> / {part ? part.title : ''}</nav>
      <h1>{guide.title}</h1>
      <p className="gd-summary">{guide.summary}</p>
      {seo.intro && <p className="gd-intro">{seo.intro}</p>}
      <div className="gd-guide-link">Link to this guide: <code>{`${SITE}/guide/${guide.slug}`}</code> <CopyLink url={`${SITE}/guide/${guide.slug}`} /></div>
      <ol className="gd-toc">{guide.steps.map((s) => <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>)}</ol>
      {guide.steps.map((step, i) => (
        <section key={step.id} id={step.id} className="gd-step">
          <div className="gd-step-head">
            <span className="gd-step-no">{i + 1}</span>
            <h2>{step.title}</h2>
            <CopyLink url={`${SITE}/guide/${guide.slug}#${step.id}`} />
          </div>
          <Points points={step.points} />
          {step.shots && <Shots guide={guide} step={step} />}
        </section>
      ))}
      <Faqs faqs={seo.faqs} />
      {related.length > 0 && (
        <section className="gd-related">
          <h2>Related guides</h2>
          <ul>{related.map((g) => <li key={g.slug}><a href={`/guide/${g.slug}`}>{g.title}</a>: {g.summary}</li>)}</ul>
        </section>
      )}
      <div className="gd-pager">
        {prev ? <a href={`/guide/${prev.slug}`}>← {prev.title}</a> : <span />}
        {next ? <a href={`/guide/${next.slug}`}>{next.title} →</a> : <span />}
      </div>
    </article>
  );
}

export default function GuidePage({ slug = '' }) {
  const guide = slug ? guideBySlug(slug) : null;

  useEffect(() => {
    const seo = guide ? GUIDE_SEO[guide.slug] || { title: `${guide.title} | Ownezzy User Guide`, description: guide.summary } : INDEX_SEO;
    const url = `${SITE}/guide${guide ? `/${guide.slug}` : ''}`;
    setPageMeta(seo.title, seo.description, url);
    const data = document.createElement('script');
    data.type = 'application/ld+json';
    data.textContent = JSON.stringify(structuredData(guide, seo, url));
    const restoreSiteFaq = hideSiteFaq();
    document.head.appendChild(data);
    // Opened with #step: scroll to it once the page has drawn
    const id = window.location.hash.slice(1);
    const timer = setTimeout(() => (id ? document.getElementById(id)?.scrollIntoView({ block: 'start' }) : window.scrollTo(0, 0)), 150);
    return () => { clearTimeout(timer); data.remove(); restoreSiteFaq(); };
  }, [guide]);

  return (
    <div className="app-shell gd-page">
      <AnnouncementBar />
      <Navbar />
      <main className="container gd-layout">
        <Sidebar current={guide} />
        <div className="gd-main">
          {slug && !guide ? (
            <div className="gd-index"><h1>Guide not found</h1><p>This guide does not exist yet. <a href="/guide">See every guide →</a></p></div>
          ) : guide ? <GuideView guide={guide} /> : <GuideIndex />}
        </div>
      </main>
      <Footer />
    </div>
  );
}
